/**
 * Activates the Directus Visual Editor overlay on the live site.
 *
 * Runs ONLY when the page is loaded inside the Directus editor (an iframe, an
 * explicit `?visual-editing` flag, or a `/preview/*` route) so real visitors are
 * unaffected — `apply()` no-ops without a Directus parent anyway. The editable
 * `data-directus` attributes are already in the server-rendered HTML for these
 * contexts (see useVisualEditing), so `apply()` finds them on its first scan.
 * Re-applies after client navigation so newly rendered elements stay editable.
 */
export default defineNuxtPlugin((nuxtApp) => {
  const inEditor = window.self !== window.top
    || new URLSearchParams(window.location.search).has('visual-editing')
    || window.location.pathname.includes('/preview/');

  if (!inEditor) return;

  const { directusUrl } = useRuntimeConfig().public;
  if (!directusUrl) return;

  let teardown: (() => void) | undefined;

  const applyEditing = async () => {
    const { apply, remove } = await import('@directus/visual-editing');
    if (teardown) remove();
    await apply({
      directusUrl,
      // /preview/* renders straight from useFetch, so a refetch is enough and
      // keeps scroll position; real site pages hydrate from stores, reload those.
      onSaved: () => (window.location.pathname.includes('/preview/')
        ? refreshNuxtData()
        : window.location.reload()),
    });
    teardown = remove;
  };

  nuxtApp.hook('app:mounted', applyEditing);
  // Re-scan after in-editor navigation so new elements pick up the overlay.
  nuxtApp.hook('page:finish', () => nextTick(applyEditing));
});
